// Query keys + request descriptors shared by client hooks, the imperative api
// modules, and server components (SSR prefetch). This module must stay free of
// 'use client' imports — server components import it to build the exact same
// cache keys the browser uses, so hydrated data lands where the client looks.
import type { QueryKey } from '@tanstack/react-query';
import type { ContactLifecycleFilter } from '@/types/contacts';
import { ALL_ACTIVITY_TYPES, type ListActivitiesQuery } from '@/types/activities';
import type { CampaignCallbackKind, CallbackListStatus } from '@/types/campaigns';
import type { CallSearchQuery } from '@/types/telephony';
import { buildAdminNotificationLogQuery } from '@/lib/admin-notification-log';

export type ApiRequest = {
  key: QueryKey;
  path: string;
  staleTime: number;
};

export function buildQueryString(
  params: Record<string, string | number | boolean | null | undefined>
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') {
      continue;
    }
    search.set(key, String(value));
  }
  const queryString = search.toString();
  return queryString ? `?${queryString}` : '';
}

export const CONTACTS_PAGE_LIMIT = 50;

export const contactsQueryKeys = {
  list: (
    search: string,
    limit: number,
    offset: number,
    campaignId: string | null,
    lifecycleStatus: string | null,
    isActive: boolean | null,
    tagId: string | null,
    ownerUserId: string | null,
    activityType: string | null,
    duplicateCandidates: boolean | null
  ) => [
    'contacts', 'list', search.trim(), limit, offset, campaignId || 'all', lifecycleStatus || 'all', isActive,
    tagId || 'all-tags', ownerUserId || 'all-owners', activityType || 'all-activities', duplicateCandidates ? 'duplicate-candidates' : 'all-duplicates',
  ] as const,
  detail: (contactId: string) => ['contacts', 'detail', contactId] as const,
  logs: (contactId: string, campaignId: string, types: string, limit: number) =>
    ['contacts', 'logs', contactId, campaignId, types, limit] as const,
  fields: (includeInactive: boolean) => ['contact-fields', includeInactive ? 'include-inactive' : 'active'] as const,
  tags: (includeInactive: boolean) => ['contact-tags', includeInactive ? 'include-inactive' : 'active'] as const,
  campaignFieldVisibility: (campaignKey: string) => ['campaign-field-visibility', campaignKey] as const,
  adminCampaignFieldVisibility: (campaignKey: string) => ['admin', 'campaign-field-visibility', campaignKey] as const,
  importJobs: (limit: number, offset: number) => ['contacts', 'import-jobs', limit, offset] as const,
  importJob: (jobId: string) => ['contacts', 'import-job', jobId] as const,
};

export type ContactListParams = {
  search: string;
  limit: number;
  offset: number;
  campaignId: string | null;
  lifecycleStatus: ContactLifecycleFilter | null;
  isActive: boolean | null;
  tagId: string | null;
  ownerUserId: string | null;
  activityType: string | null;
  duplicateCandidates: boolean;
};

export function contactListRequest(params: ContactListParams): ApiRequest {
  const search = params.search.trim();
  return {
    key: contactsQueryKeys.list(
      search,
      params.limit,
      params.offset,
      params.campaignId,
      params.lifecycleStatus,
      params.isActive,
      params.tagId,
      params.ownerUserId,
      params.activityType,
      params.duplicateCandidates
    ),
    path: `/api/contacts${buildQueryString({
      limit: params.limit,
      offset: params.offset,
      search,
      campaignId: params.campaignId,
      lifecycleStatus: params.lifecycleStatus,
      isActive: params.isActive,
      tagId: params.tagId,
      ownerUserId: params.ownerUserId,
      activityType: params.activityType,
      duplicateCandidates: params.duplicateCandidates || undefined,
    })}`,
    staleTime: 1_500,
  };
}

export function contactTagsRequest(): ApiRequest {
  return { key: contactsQueryKeys.tags(false), path: '/api/contact-tags', staleTime: 10_000 };
}

export function contactQuickSearchRequest(search: string, limit: number): ApiRequest {
  const q = search.trim();
  return {
    key: ['contacts', 'quick-search', q, limit],
    path: `/api/contacts/quick-search${buildQueryString({ q, limit })}`,
    staleTime: 30_000,
  };
}

export const campaignQueryKeys = {
  list: ['campaigns', 'list'] as const,
  detail: (campaignId: string) => ['campaign', campaignId, 'detail'] as const,
  dialReadiness: (campaignId: string) => ['campaign', campaignId, 'dial-readiness'] as const,
  accessUsers: (campaignId: string) => ['campaign', campaignId, 'access-users'] as const,
  assignableAgents: (campaignId: string) => ['campaign', campaignId, 'assignable-agents'] as const,
  dialerBootstrap: (campaignId: string) => ['campaign', campaignId, 'dialer-bootstrap'] as const,
  dialerResume: (campaignId: string) => ['campaign', campaignId, 'dialer-resume'] as const,
  callbacksList: (
    status: CallbackListStatus,
    campaignId: string | null,
    ownerUserId: string | null,
    kind: CampaignCallbackKind | null,
    search: string,
    limit: number,
    offset: number
  ) => ['callbacks', status, campaignId || 'all', ownerUserId || 'all', kind || 'all', search, limit, offset] as const,
  contactPendingCallback: (campaignId: string, contactId: string) =>
    ['campaign', campaignId, 'contact', contactId, 'pending-callback'] as const,
  contactNotes: (campaignId: string, contactId: string) => ['campaign', campaignId, 'contact', contactId, 'notes'] as const,
  dialerSettings: ['dialer', 'settings'] as const,
  products: ['products', 'list'] as const,
};

export function campaignListRequest(): ApiRequest {
  return { key: campaignQueryKeys.list, path: '/api/campaigns', staleTime: 1_500 };
}

export function productListRequest(includeInactive: boolean): ApiRequest {
  return includeInactive
    ? { key: [...campaignQueryKeys.products, 'all'], path: '/api/products?includeInactive=true', staleTime: 10_000 }
    : { key: campaignQueryKeys.products, path: '/api/products', staleTime: 10_000 };
}

export const dealQueryKeys = {
  list: (queryKey: string) => ['deals', 'list', queryKey] as const,
  detail: (dealId: string) => ['deals', 'detail', dealId] as const,
  attachments: (dealId: string) => ['deals', 'detail', dealId, 'attachments'] as const,
  boards: ['deals', 'boards'] as const,
  boardUsers: ['deals', 'board-users'] as const,
  labels: ['deals', 'labels'] as const,
};

export function dealDetailRequest(dealId: string): ApiRequest {
  return { key: dealQueryKeys.detail(dealId), path: `/api/deals/${dealId}`, staleTime: 1_500 };
}

// staleTime 0: contact edits made elsewhere (workspace, dialer) must show on the next read.
export function contactDetailRequest(contactId: string): ApiRequest {
  return { key: contactsQueryKeys.detail(contactId), path: `/api/contacts/${contactId}`, staleTime: 0 };
}

// The contact card is the pooled dialer's screen pop: it is fetched while the
// agent is already live with the prospect, over whatever link that agent has,
// and every log entry in the payload is bytes standing between them and the
// prospect's details. The limit is part of the query key, so the SSR prefetch
// in app/contacts/[id]/page.tsx and the client query in contact-detail-page
// must always pass the SAME one or the prefetched page is orphaned and the
// client refetches from scratch — the opposite of the point.

/** Contact history on first paint: the recent window an agent actually reads
 * mid-call. The rest loads when they ask for it. */
export const CONTACT_LOG_INITIAL_LIMIT = 50;

/** The full history window, fetched on demand from the History panel. */
export const CONTACT_LOG_FULL_LIMIT = 300;

export function contactLogsRequest(
  contactId: string,
  options: { campaignId?: string | null; types?: string[]; limit?: number } = {}
): ApiRequest {
  const campaignId = options.campaignId || 'all';
  const types = (options.types ?? []).join(',');
  const limit = options.limit ?? 200;
  return {
    key: contactsQueryKeys.logs(contactId, String(campaignId), types, limit),
    path: `/api/contacts/${contactId}/logs${buildQueryString({
      campaignId: campaignId !== 'all' ? campaignId : undefined,
      types,
      limit,
    })}`,
    // Small stale window: during rapid dialing the same contact's logs are
    // read by several triggers in quick succession; dedup them off the wire.
    staleTime: 3_000,
  };
}

export function dealBoardsRequest(): ApiRequest {
  return { key: dealQueryKeys.boards, path: '/api/deals/boards', staleTime: 15_000 };
}

export function dealBoardUsersRequest(): ApiRequest {
  return { key: dealQueryKeys.boardUsers, path: '/api/deals/board-users', staleTime: 5_000 };
}

export function dealLabelsRequest(): ApiRequest {
  return { key: dealQueryKeys.labels, path: '/api/deals/labels', staleTime: 5_000 };
}

export const adminUsersQueryKeys = {
  list: ['admin', 'users', 'list'] as const,
};

export type MeetingRecordingsListParams = {
  from: string | null;
  to: string | null;
  closerUserId: string | null;
  q: string;
  limit: number;
  offset: number;
};

export function meetingRecordingsListRequest(params: MeetingRecordingsListParams): ApiRequest {
  const q = params.q.trim();
  return {
    key: [
      'meeting-recordings', 'list', params.from ?? '', params.to ?? '',
      params.closerUserId ?? 'all', q, params.limit, params.offset,
    ] as const,
    path: `/api/meetings/recordings${buildQueryString({
      from: params.from,
      to: params.to,
      closerUserId: params.closerUserId,
      q,
      limit: params.limit,
      offset: params.offset,
    })}`,
    staleTime: 5_000,
  };
}

export function adminUsersRequest(): ApiRequest {
  return { key: adminUsersQueryKeys.list, path: '/api/admin/users', staleTime: 2_000 };
}

// Same key/endpoint as use-app-timezone's useQuery; its 60s staleTime governs freshness.
export function appTimezoneRequest(): ApiRequest {
  return { key: ['app', 'timezone'], path: '/api/meetings/settings', staleTime: 60_000 };
}

// Region settings change only by an admin save, which refreshes this key.
export function regionConfigRequest(): ApiRequest {
  return { key: ['region', 'config'], path: '/api/region/config', staleTime: 10 * 60_000 };
}

// Mirrors marketsApi.listOperatingCompanies (markets.ts): same key and endpoint,
// includeInactive=true. That imperative loader passes no staleTime, so it reads
// on the client's DEFAULT_QUERY_STALE_MS (30s) window — matched here so the
// hydrated value is served without a client refetch.
export function operatingCompaniesRequest(): ApiRequest {
  return {
    key: ['admin', 'markets', 'operating-companies'],
    path: '/api/admin/markets/operating-companies?includeInactive=true',
    staleTime: 30_000,
  };
}

// Client-telemetry admin dashboard: three independent queries prefetched at
// their default (no-filter, first-page, 24h) variants so the page paints from
// the first HTML response; the live 60s refetch takes over after hydration.
// staleTime 0 keeps the "always live" behaviour these queries already have.
export const CLIENT_TELEMETRY_EVENTS_PAGE_SIZE = 50;

export const clientTelemetryQueryKeys = {
  issues: ['client-telemetry', 'issues'] as const,
  summary: (windowHours: number) => ['client-telemetry', 'summary', windowHours] as const,
  events: (userId: string, eventName: string, severity: string, page: number) =>
    ['client-telemetry', 'events', userId, eventName, severity, page] as const,
};

export function clientTelemetryIssuesRequest(): ApiRequest {
  return { key: clientTelemetryQueryKeys.issues, path: '/api/admin/telemetry/client/issues', staleTime: 0 };
}

export function clientTelemetrySummaryRequest(windowHours: number): ApiRequest {
  return {
    key: clientTelemetryQueryKeys.summary(windowHours),
    path: `/api/admin/telemetry/client/summary${buildQueryString({ windowHours })}`,
    staleTime: 0,
  };
}

export function clientTelemetryEventsRequest(
  params: { userId?: string; eventName?: string; severity?: string; page: number }
): ApiRequest {
  const userId = params.userId ?? '';
  const eventName = params.eventName ?? '';
  const severity = params.severity ?? '';
  return {
    key: clientTelemetryQueryKeys.events(userId, eventName, severity, params.page),
    path: `/api/admin/telemetry/client/events${buildQueryString({
      userId: userId || undefined,
      eventName: eventName || undefined,
      severity: severity || undefined,
      limit: CLIENT_TELEMETRY_EVENTS_PAGE_SIZE,
      offset: params.page * CLIENT_TELEMETRY_EVENTS_PAGE_SIZE,
    })}`,
    staleTime: 0,
  };
}

// Recent-calls search — hoisted here (server-safe) so the /calls server page and
// the client sipApi.searchCalls build the exact same key+path. staleTime 2s
// matches the imperative caller's dedup window.
export const RECENT_CALLS_PAGE_SIZE = 50;

function buildCallSearchQueryString(query: CallSearchQuery): string {
  return buildQueryString({
    q: query.q?.trim(),
    status: query.status && query.status !== 'all' ? query.status : undefined,
    source: query.source?.trim(),
    dialMode: query.dialMode && query.dialMode !== 'all' ? query.dialMode : undefined,
    startDate: query.startDate?.trim(),
    endDate: query.endDate?.trim(),
    timezone: query.timezone?.trim(),
    campaignId: query.campaignId?.trim(),
    agentUserIds: query.agentUserIds?.length ? query.agentUserIds.join(',') : undefined,
    agentUserId: query.agentUserIds?.length ? undefined : query.agentUserId?.trim(),
    limit: query.limit,
    offset: query.offset,
  });
}

export function callsSearchRequest(query: CallSearchQuery): ApiRequest {
  const queryString = buildCallSearchQueryString(query);
  return { key: ['sip', 'calls-search', queryString], path: `/api/sip/calls/search${queryString}`, staleTime: 2_000 };
}

// Notification log — the /notifications page prefetches the DEFAULT variant its
// first render asks for (EMPTY_FILTERS + offset 0). The page branches on role:
// leadership reads the admin log endpoint, everyone else their personal feed.
// Keys/paths mirror notificationsApi.listAdminNotificationLog / listNotifications
// exactly so the imperative loader's fetchCachedQuery reads the hydrated cache
// with no wire round trip. PAGE_SIZE mirrors the component's PAGE_SIZE.
const NOTIFICATIONS_PAGE_SIZE = 100;

// Personal feed default: limit=NOTIFICATIONS_PAGE_SIZE, offset=0, unreadOnly
// false (EMPTY_FILTERS.readState is '' → 'all'). Mirrors listNotifications.
export function notificationLogRequest(): ApiRequest {
  const limit = NOTIFICATIONS_PAGE_SIZE;
  return {
    key: ['notifications', 'list', limit, 0, 'all'] as const,
    path: `/api/notifications${buildQueryString({ limit, offset: 0 })}`,
    staleTime: 1_500,
  };
}

// Leadership admin-log default: no filters, offset 0. Reuses
// buildAdminNotificationLogQuery so the querystring (and thus the cache key)
// matches listAdminNotificationLog byte-for-byte.
export function adminNotificationLogRequest(): ApiRequest {
  const query = buildAdminNotificationLogQuery({ limit: NOTIFICATIONS_PAGE_SIZE, offset: 0 });
  return {
    key: ['admin', 'notifications', 'log', query] as const,
    path: `/api/admin/notifications?${query}`,
    staleTime: 1_500,
  };
}

export const ACTIVITIES_LIST_PAGE_SIZE = 50;

const activitiesQueryKeys = {
  list: (queryString: string) => ['activities', 'list', queryString] as const,
};

/**
 * Single source of truth for the /activities list key+path — SSR prefetch and
 * the client api module both build from here, so hydrated data lands exactly
 * where the client looks.
 */
export function activitiesListRequest(query: ListActivitiesQuery): ApiRequest {
  const queryString = buildQueryString({
    from: query.from ?? undefined,
    to: query.to ?? undefined,
    status: query.status,
    types: query.types && query.types.length ? query.types.join(',') : undefined,
    ownerUserId: query.ownerUserId ?? undefined,
    q: query.q ?? undefined,
    callbackKinds: query.callbackKinds && query.callbackKinds.length ? query.callbackKinds.join(',') : undefined,
    followupTypes: query.followupTypes && query.followupTypes.length ? query.followupTypes.join(',') : undefined,
    meetingStatuses: query.meetingStatuses && query.meetingStatuses.length ? query.meetingStatuses.join(',') : undefined,
    meetingProgression: query.meetingProgression ?? undefined,
    limit: query.limit,
    offset: query.offset,
  });
  return { key: activitiesQueryKeys.list(queryString), path: `/activities${queryString}`, staleTime: 1_500 };
}

/**
 * The deterministic first-render list query (defaultFilters + the 'todo'
 * preset): every type, no sub-filters, no date window. Must mirror
 * use-activities-state's defaults exactly — persisted preferences are adopted
 * in a mount effect AFTER this state renders (the #645 hydration rule), so
 * this is what the client asks for first and what SSR prefetches.
 */
export function defaultActivitiesListQuery(): ListActivitiesQuery {
  return {
    types: ALL_ACTIVITY_TYPES,
    q: null,
    callbackKinds: [],
    followupTypes: [],
    meetingStatuses: [],
    meetingProgression: null,
    from: null,
    to: null,
    status: 'undone',
    limit: ACTIVITIES_LIST_PAGE_SIZE,
    offset: 0,
  };
}
